import type { CSSProperties } from 'react';
import { INE_CONTACT_SUPPORT } from 'constants/settings';
import { pluralize } from 'utils/string';
import { licenseChange } from 'constants/licenses';

export enum SkillDiveToggleReason {
  NO_TRAINING = 'no_training',
  NOT_ON_PLAN = 'not_on_plan',
  EXHAUSTED = 'exhausted',
  NON_TRANSFERABLE = 'non_transferable',
}

export const SKILL_DIVE_TOGGLE_TOOLTIPS: Record<SkillDiveToggleReason, string> = {
  [SkillDiveToggleReason.NO_TRAINING]: 'User must have a training license to assign Skill Dive',
  [SkillDiveToggleReason.NOT_ON_PLAN]:
    'Skill Dive is not enabled for your account. Contact your account manager to add it.',
  [SkillDiveToggleReason.EXHAUSTED]:
    'Your account has no remaining Skill Dive licenses available. Contact Sales to add additional licenses.',
  [SkillDiveToggleReason.NON_TRANSFERABLE]:
    'Skill Dive licenses are non-transferable after assigning to a user',
};

// Staff see another account, so swap the "your account" / "contact Sales" wording.
export const SKILL_DIVE_STAFF_TOGGLE_TOOLTIPS: Partial<Record<SkillDiveToggleReason, string>> = {
  [SkillDiveToggleReason.NOT_ON_PLAN]: 'This account has no Skill Dive add-on on its plan',
  [SkillDiveToggleReason.EXHAUSTED]: 'This account has no remaining Skill Dive licenses available',
};

export const SKILL_DIVE_ASSIGN_TOOLTIP = 'Assign Skill Dive license';
export const SKILL_DIVE_REMOVE_TOOLTIP = 'Remove Skill Dive license';

// Skill Dive tooltips sit above the toggle and wrap, so the longer copy stays inside the modal.
export const SKILL_DIVE_TOOLTIP_STYLE: CSSProperties = { maxWidth: 221, textAlign: 'center' };

// The design breaks these shorter tooltips onto two lines.
export const NOT_ON_PLAN_TOOLTIP_STYLE: CSSProperties = { maxWidth: 112, textAlign: 'center' };
export const NO_TRAINING_TOOLTIP_STYLE: CSSProperties = { maxWidth: 145, textAlign: 'center' };

// Whether a new invite link grants Skill Dive by default. Product hasn't settled this yet, so it
// lives here as the one value to change.
export const ASSIGN_SKILL_DIVE_LINK_DEFAULT = false;

// The out-of-seats banner asks INE for more, like the training one.
export const SKILL_DIVE_CONTACT_HREF = `mailto:${INE_CONTACT_SUPPORT}`;

// Where the not-on-plan links go (PRD: Skill Dive License Flow, requirement 7).
export const SKILL_DIVE_LEARN_MORE_URL = 'https://ine.com/dive';

export const SKILL_DIVE_LEARN_MORE = {
  HEADER: 'Learn more',
  LINK: 'Learn more about Skill Dive',
} as const;

export const SKILL_DIVE_NOT_ON_PLAN = {
  LABEL: 'Not included in your plan',
  QUALIFIER: '(not included in your plan)',
} as const;

export const NO_SKILL_DIVE_LICENSES_BANNER = {
  TITLE: 'No Skill Dive licenses available',
  BODY: 'Invitees with Skill Dive turned on will get it. You can still invite the rest without Skill Dive.',
  BUTTON: 'Contact us about Skill Dive',
} as const;

export const SKILL_DIVE_ACTION_LABELS = {
  ASSIGN: 'Assign Skill Dive',
  REMOVE: 'Remove Skill Dive',
} as const;

export const skillDiveLicenseChanged = (assign: boolean): string =>
  `Skill Dive license ${licenseChange(assign)}`;

export const skillDiveLicenseChangeFailed = (assign: boolean): string =>
  `Skill Dive license couldn’t be ${licenseChange(assign)}`;

export const SKILL_DIVE_NOT_ASSIGNED_WITHOUT_TRAINING =
  'Skill Dive license couldn’t be assigned without a training license';

export const SKILL_DIVE_LICENSES_UPDATED = 'Skill Dive licenses updated';
export const SKILL_DIVE_LICENSES_UPDATE_FAILED = 'Skill Dive licenses couldn’t be updated';
export const SKILL_DIVE_LICENSES_NO_LONGER_CHANGEABLE =
  'Skill Dive licenses couldn’t be updated: you can no longer change the selected invitations’ Skill Dive licenses';

// Appended to the "Remove License" confirm message, so it starts with a space.
export const SKILL_DIVE_REMOVED_WITH_LICENSE_NOTE = ' Their Skill Dive licenses are removed too.';

// Appended to the "Assign Skill Dive" confirm message, so it starts with a space.
export const SKILL_DIVE_NON_TRANSFERABLE_NOTE = ` ${
  SKILL_DIVE_TOGGLE_TOOLTIPS[SkillDiveToggleReason.NON_TRANSFERABLE]
}.`;

export const assignSkillDiveTitle = (invitations: number): string =>
  `Assign Skill Dive License${invitations > 1 ? 's' : ''}`;

export const assignSkillDiveDescription = (invitations: number): string =>
  `Are you sure you want to assign Skill Dive to ${pluralize(invitations, 'selected invitation')}?`;

// Appended to the assign confirm message, so it starts with a space.
export const skippedWithoutTrainingNote = (skipped: number): string =>
  skipped > 0
    ? ` ${pluralize(skipped, 'selected invitation')} ${
        skipped > 1 ? 'have' : 'has'
      } no training license and will be skipped.`
    : '';

export const ASSIGN_TOO_MANY_SKILL_DIVE_TITLE = 'Assign Skill Dive Licenses';

export const assignTooManySkillDiveDescription = (selected: number, available: number): string =>
  `You selected ${pluralize(selected, 'invitation')}, but you only have ${pluralize(
    available,
    'Skill Dive license'
  )} available. Please, choose fewer than ${pluralize(selected, 'invitation')} to continue.`;

export const removeSkillDiveTitle = (invitations: number): string =>
  `Remove Skill Dive License${invitations > 1 ? 's' : ''}`;

export const removeSkillDiveDescription = (invitations: number): string =>
  `Are you sure you want to remove Skill Dive from ${pluralize(
    invitations,
    'selected invitation'
  )}? The licenses go back to your available Skill Dive licenses.`;

export const skillDiveSwitchLabel = (email: string): string => `Skill Dive for ${email}`;
